import { useRolodexEdit } from 'features/fde'
import { useKestrel } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { useMemo, useState } from 'react'
import { KVAnnotation, RolodexEntryPatchJson } from 'types'

export const useAnnotationSelection = () => {
  const {
    kestrelModel,
    annotations,
    setAnnotations,
    sendKVCommand,
    selectedMarkup,
    setSelectedMarkup,
    annotationSortKey,
  } = useKestrel()
  const { rolodexPatch } = useRolodexEdit()

  const [selectedAnnotationIds, setSelectedAnnotationIds] = useState<
    Set<string>
  >(new Set())
  const [lastClickedAnnotationId, setLastClickedAnnotationId] = useState<
    string | null
  >(null)
  const [isAnnotationsSectionDragOver, setIsAnnotationsSectionDragOver] =
    useState(false)
  const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState(false)

  const handleAnnotationClick = (
    annotationId: string,
    isShiftClick: boolean = false
  ) => {
    if (isShiftClick && lastClickedAnnotationId && annotations) {
      // Shift-click: select range from last clicked to current
      const annotationIds = annotations.map((a) => a.id)
      const lastIndex = annotationIds.indexOf(lastClickedAnnotationId)
      const currentIndex = annotationIds.indexOf(annotationId)

      if (lastIndex !== -1 && currentIndex !== -1) {
        const startIndex = Math.min(lastIndex, currentIndex)
        const endIndex = Math.max(lastIndex, currentIndex)
        const rangeIds = annotationIds.slice(startIndex, endIndex + 1)

        const newSelection = new Set(selectedAnnotationIds)
        rangeIds.forEach((id) => newSelection.add(id))
        setSelectedAnnotationIds(newSelection)
      }
    } else {
      // Ctrl/Cmd-click: toggle single selection
      const newSelection = new Set(selectedAnnotationIds)
      if (newSelection.has(annotationId)) {
        newSelection.delete(annotationId)
      } else {
        newSelection.add(annotationId)
      }
      setSelectedAnnotationIds(newSelection)
      setLastClickedAnnotationId(annotationId)
    }
  }

  const handleClearSelection = () => {
    setSelectedAnnotationIds(new Set())
    setLastClickedAnnotationId(null)
  }

  const handleDropAnnotations = (
    annotationIds: string[],
    targetTagValue: string | null
  ) => {
    if (!annotations) return

    // Directly modify the annotation objects to maintain their class instances
    annotations.forEach((annotation) => {
      if (annotationIds.includes(annotation.id)) {
        annotation.tags[annotationSortKey] = {
          value: targetTagValue || '',
          derivedTag: false,
        }
      }
    })

    // Trigger state update with new array reference
    setAnnotations([...annotations])

    // Update Rolodex
    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel?.id ?? '',
      data: {
        annotations: annotations.map((a) => KVAnnotation.toJSON(a)),
      },
    }
    rolodexPatch([patchData])

    // Clear multi-select after drop
    setSelectedAnnotationIds(new Set())
    setLastClickedAnnotationId(null)
  }

  const handleReorderAnnotations = (
    draggedIds: string[],
    targetId: string,
    position: 'before' | 'after'
  ) => {
    if (!annotations) return

    // Find the target annotation to get its group
    const targetAnnotation = annotations.find((a) => a.id === targetId)
    if (!targetAnnotation) return

    const targetTag = targetAnnotation.tags[annotationSortKey]

    // Get the annotations to move and update their group
    const annotationsToMove = annotations.filter((a) =>
      draggedIds.includes(a.id)
    )

    // Update the group for all dragged annotations
    annotationsToMove.forEach((annotation) => {
      annotation.tags[annotationSortKey] = { ...targetTag }
    })

    // Remove dragged annotations from array
    const remainingAnnotations = annotations.filter(
      (a) => !draggedIds.includes(a.id)
    )

    // Find the target index in the remaining array
    const targetIndex = remainingAnnotations.findIndex((a) => a.id === targetId)
    if (targetIndex === -1) return

    // Insert at the correct position
    const insertIndex = position === 'before' ? targetIndex : targetIndex + 1
    const reorderedAnnotations = [
      ...remainingAnnotations.slice(0, insertIndex),
      ...annotationsToMove,
      ...remainingAnnotations.slice(insertIndex),
    ]

    setAnnotations(reorderedAnnotations)

    // Update Rolodex
    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel?.id ?? '',
      data: {
        annotations: reorderedAnnotations.map((a) => KVAnnotation.toJSON(a)),
      },
    }
    rolodexPatch([patchData])

    // Clear multi-select after reorder
    setSelectedAnnotationIds(new Set())
    setLastClickedAnnotationId(null)
  }

  const handleAnnotationsSectionDragOver = (e: React.DragEvent) => {
    // Only handle if dragging annotations
    if (!e.dataTransfer.types.includes('annotationids')) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
    setIsAnnotationsSectionDragOver(true)
  }

  const handleAnnotationsSectionDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsAnnotationsSectionDragOver(false)
  }

  const handleAnnotationsSectionDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsAnnotationsSectionDragOver(false)

    const annotationIds = e.dataTransfer.getData('annotationIds')
    if (annotationIds) {
      const ids = JSON.parse(annotationIds)
      handleDropAnnotations(ids, null)
    }
  }

  const handleBatchDelete = () => {
    setShowBatchDeleteConfirm(true)
  }

  const handleBatchDeleteConfirm = () => {
    if (!annotations || !sendKVCommand) return

    const annotationsToDelete = annotations.filter((a) =>
      selectedAnnotationIds.has(a.id)
    )

    const filteredAnnotations = annotations.filter(
      (a) => !selectedAnnotationIds.has(a.id)
    )

    setAnnotations(filteredAnnotations)

    // Send delete commands for each annotation
    annotationsToDelete.forEach((annotation) => {
      if (annotation.geometry?.kind === 'point') {
        sendKVCommand({
          command: KestrelCommands.DELETE_ANNOTATION,
          annotationId: annotation.id,
        })
      } else if (annotation.geometry?.kind === 'polygon') {
        sendKVCommand({
          command: KestrelCommands.DELETE_SURFACE_AREA,
          surfaceAreaId: annotation.id,
        })
      }
    })

    // Clear selection if any selected annotation was deleted
    if (selectedAnnotationIds.has(selectedMarkup?.id ?? '')) {
      setSelectedMarkup(undefined)
    }

    // Update Rolodex
    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel?.id ?? '',
      data: {
        annotations:
          filteredAnnotations?.map((a) => KVAnnotation.toJSON(a)) ?? [],
      },
    }
    rolodexPatch([patchData])

    // Clear multi-select
    setSelectedAnnotationIds(new Set())
    setLastClickedAnnotationId(null)
    setShowBatchDeleteConfirm(false)
  }

  const handleBatchDeleteCancel = () => {
    setShowBatchDeleteConfirm(false)
  }

  const batchDeleteMessage = useMemo(() => {
    if (selectedAnnotationIds.size === 0) return ''

    const annotationsToDelete = annotations?.filter((a) =>
      selectedAnnotationIds.has(a.id)
    )
    const labels = annotationsToDelete?.map((a) => a.label || 'Untitled') || []

    // Show first 5 labels, then "and X more..."
    const displayLabels =
      labels.length > 5
        ? `${labels.slice(0, 5).join(', ')} and ${labels.length - 5} more...`
        : labels.join(', ')

    return `Are you sure you want to delete ${selectedAnnotationIds.size} annotation${selectedAnnotationIds.size > 1 ? 's' : ''}?\n\n${displayLabels}`
  }, [annotations, selectedAnnotationIds])

  return {
    selectedAnnotationIds,
    isAnnotationsSectionDragOver,
    showBatchDeleteConfirm,
    batchDeleteMessage,
    handleAnnotationClick,
    handleClearSelection,
    handleDropAnnotations,
    handleReorderAnnotations,
    handleAnnotationsSectionDragOver,
    handleAnnotationsSectionDragLeave,
    handleAnnotationsSectionDrop,
    handleBatchDelete,
    handleBatchDeleteConfirm,
    handleBatchDeleteCancel,
  }
}
